import { useEffect, useRef, useState } from 'react';
import './PrivacyPolicy.scss';
import { getLanguage } from '../../utils/Helper';

const PrivacyPolicy = () => {
  const [isVisible, setIsVisible] = useState({});
  const sectionsRef = useRef([]);
  const lang = getLanguage();

  const content = {
    EN: {
      hero: {
        label: "Legal",
        title: "Privacy Policy",
        tagline: "Your Privacy Matters",
        description: "At Addmix Store, we are fully committed to protecting your personal information and being transparent about how we handle your data.",
        updated: "Last Updated: 2024"
      },
      intro: {
        title: "Our Commitment to You",
        text: "This Privacy Policy explains how Addmix Store collects, uses, and protects your information when you visit or order from our site. We believe in complete transparency — you deserve to know exactly how your data is handled."
      },
      collect: {
        title: "What We Collect",
        icon: "📋",
        items: [
          {
            icon: "👤",
            title: "Personal Contact Information",
            desc: "Your name, email address, and phone number to identify your account and reach you when needed."
          },
          {
            icon: "📦",
            title: "Order Information",
            desc: "Shipping address and product details to ensure your orders are processed and delivered accurately."
          },
          {
            icon: "🔒",
            title: "Payment Details",
            desc: "All payment information is securely encrypted and protected — we never store raw card data."
          }
        ]
      },
      usage: {
        title: "How We Use It",
        icon: "⚙️",
        items: [
          {
            icon: "✅",
            title: "Process & Deliver Orders",
            desc: "We use your data to fulfill your purchases and ensure timely, accurate delivery to your door."
          },
          {
            icon: "📩",
            title: "Order Confirmations & Updates",
            desc: "We send you order confirmations and shipping updates so you're always in the loop."
          },
          {
            icon: "📈",
            title: "Improve Your Experience",
            desc: "We analyze usage patterns to continuously improve our service and your shopping experience."
          }
        ]
      },
      security: {
        title: "Security",
        text: "We use strong, industry-standard security measures to protect your data at every step — from collection to storage. Your trust is our responsibility."
      },
      rights: {
        title: "Your Rights",
        items: [
          { icon: "👁️", title: "Access", desc: "Request a copy of the data we hold about you." },
          { icon: "✏️", title: "Correction", desc: "Update or correct any inaccurate personal information." },
          { icon: "🗑️", title: "Deletion", desc: "Request deletion of your personal data at any time." },
          { icon: "🚫", title: "Opt-Out", desc: "Unsubscribe from marketing communications anytime." }
        ]
      },
      contact: {
        title: "Questions About Privacy?",
        text: "If you have any questions or concerns about our privacy practices, don't hesitate to reach out. We're always here to help.",
        cta: "Visit Our Contact Us Page"
      }
    },
    AR: {
      hero: {
        label: "قانوني",
        title: "سياسة الخصوصية",
        tagline: "خصوصيتك أولويتنا",
        description: "في أددميكس ستور، ملتزمون بشكل كامل بحماية معلوماتك الشخصية والشفافية التامة في التعامل مع بياناتك.",
        updated: "آخر تحديث: 2024"
      },
      intro: {
        title: "التزامنا تجاهك",
        text: "سياسة الخصوصية دي بتشرح إزاي أددميكس ستور بتجمع وبتستخدم وبتحمي معلوماتك لما بتزور موقعنا أو بتطلب منه. إحنا مؤمنين بالشفافية الكاملة — إنت مستحق تعرف بالظبط إزاي بنتعامل مع بياناتك."
      },
      collect: {
        title: "إيه اللي بنجمعه",
        icon: "📋",
        items: [
          {
            icon: "👤",
            title: "معلومات التواصل الشخصية",
            desc: "اسمك، إيميلك، ورقم تليفونك عشان نتعرف على حسابك ونتواصل معاك لما يتطلب الأمر."
          },
          {
            icon: "📦",
            title: "معلومات الطلب",
            desc: "عنوان الشحن وتفاصيل المنتج عشان نتأكد إن طلباتك اتعملت وبتتوصل بشكل صح."
          },
          {
            icon: "🔒",
            title: "تفاصيل الدفع",
            desc: "كل بيانات الدفع مشفرة ومحمية بشكل آمن — إحنا مش بنحتفظ ببيانات البطاقة الخام أبداً."
          }
        ]
      },
      usage: {
        title: "إزاي بنستخدمها",
        icon: "⚙️",
        items: [
          {
            icon: "✅",
            title: "معالجة وتوصيل الطلبات",
            desc: "بنستخدم بياناتك عشان ننفذ مشترياتك ونضمن التوصيل السريع والدقيق لحد بابك."
          },
          {
            icon: "📩",
            title: "تأكيدات الطلبات والتحديثات",
            desc: "بنبعتلك تأكيدات الطلبات وتحديثات الشحن عشان تكون دايماً على علم بكل حاجة."
          },
          {
            icon: "📈",
            title: "تحسين تجربتك",
            desc: "بنحلل أنماط الاستخدام عشان نحسن خدمتنا وتجربة التسوق بتاعتك باستمرار."
          }
        ]
      },
      security: {
        title: "الأمان",
        text: "بنستخدم إجراءات أمنية قوية وفق معايير الصناعة عشان نحمي بياناتك في كل خطوة — من الجمع للتخزين. ثقتك هي مسؤوليتنا."
      },
      rights: {
        title: "حقوقك",
        items: [
          { icon: "👁️", title: "الوصول", desc: "اطلب نسخة من البيانات اللي عندنا عنك." },
          { icon: "✏️", title: "التصحيح", desc: "حدّث أو صحح أي معلومات شخصية غلط." },
          { icon: "🗑️", title: "الحذف", desc: "اطلب حذف بياناتك الشخصية في أي وقت." },
          { icon: "🚫", title: "إلغاء الاشتراك", desc: "إلغي الاشتراك من الرسائل التسويقية في أي وقت." }
        ]
      },
      contact: {
        title: "عندك أسئلة عن الخصوصية؟",
        text: "لو عندك أي أسئلة أو مخاوف بخصوص سياسة الخصوصية بتاعتنا، متترددش تتواصل معانا. إحنا دايماً هنا نساعدك.",
        cta: "زور صفحة التواصل معانا"
      }
    }
  };

  const currentContent = content[lang] || content.EN;

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible((prev) => ({
              ...prev,
              [entry.target.dataset.section]: true
            }));
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -80px 0px' }
    );

    sectionsRef.current.forEach((section) => {
      if (section) observer.observe(section);
    });

    return () => observer.disconnect();
  }, []);

  const addToRefs = (el) => {
    if (el && !sectionsRef.current.includes(el)) {
      sectionsRef.current.push(el);
    }
  };

  return (
    <div className={`privacy-page ${lang === 'AR' ? 'rtl' : 'ltr'}`}>

      {/* Hero Section */}
      <section className="privacy-hero-section" data-section="hero" ref={addToRefs}>
        <div className="privacy-hero-background">
          <div className="privacy-orb orb-1"></div>
          <div className="privacy-orb orb-2"></div>
          <div className="privacy-orb orb-3"></div>
          <div className="shield-bg">🔒</div>
        </div>

        <div className="privacy-hero-content">
          <div className="privacy-hero-text">
            <span className="privacy-label">{currentContent.hero.label}</span>
            <h1 className="privacy-title">{currentContent.hero.title}</h1>
            <p className="privacy-tagline">{currentContent.hero.tagline}</p>
            <p className="privacy-description">{currentContent.hero.description}</p>
            <div className="privacy-badge">
              <span>{currentContent.hero.updated}</span>
            </div>
          </div>
        </div>

        <div className="scroll-indicator">
          <div className="mouse">
            <div className="wheel"></div>
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <section
        className={`pp-intro-section ${isVisible.intro ? 'visible' : ''}`}
        data-section="intro"
        ref={addToRefs}
      >
        <div className="glass-card">
          <div className="card-glow"></div>
          <h2 className="section-title">{currentContent.intro.title}</h2>
          <p className="section-text">{currentContent.intro.text}</p>
        </div>
      </section>

      {/* What We Collect */}
      <section
        className={`pp-collect-section ${isVisible.collect ? 'visible' : ''}`}
        data-section="collect"
        ref={addToRefs}
      >
        <h2 className="section-title">
          <span className="section-icon">{currentContent.collect.icon}</span>
          {currentContent.collect.title}
        </h2>
        <div className="pp-cards-grid">
          {currentContent.collect.items.map((item, index) => (
            <div
              key={index}
              className="pp-info-card"
              style={{ animationDelay: `${index * 0.15}s` }}
            >
              <div className="pp-card-icon">{item.icon}</div>
              <h4>{item.title}</h4>
              <p>{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How We Use It */}
      <section
        className={`pp-usage-section ${isVisible.usage ? 'visible' : ''}`}
        data-section="usage"
        ref={addToRefs}
      >
        <h2 className="section-title">
          <span className="section-icon">{currentContent.usage.icon}</span>
          {currentContent.usage.title}
        </h2>
        <div className="pp-usage-grid">
          {currentContent.usage.items.map((item, index) => (
            <div
              key={index}
              className="pp-usage-card"
              style={{ animationDelay: `${index * 0.2}s` }}
            >
              <div className="usage-number">{index + 1}</div>
              {/* <div className="usage-icon">{item.icon}</div> */}
              <h3>{item.title}</h3>
              <p>{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Security Section */}
      <section
        className={`pp-security-section ${isVisible.security ? 'visible' : ''}`}
        data-section="security"
        ref={addToRefs}
      >
        <div className="security-card">
          <div className="security-shield">🛡️</div>
          <h2>{currentContent.security.title}</h2>
          <p>{currentContent.security.text}</p>
          <div className="security-dots">
            <span></span><span></span><span></span>
          </div>
        </div>
      </section>

      {/* Your Rights */}
      <section
        className={`pp-rights-section ${isVisible.rights ? 'visible' : ''}`}
        data-section="rights"
        ref={addToRefs}
      >
        <h2 className="section-title">{currentContent.rights.title}</h2>
        <div className="rights-grid">
          {currentContent.rights.items.map((item, index) => (
            <div
              key={index}
              className="right-card"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              {/* <div className="right-icon">{item.icon}</div> */}
              <h4>{item.title}</h4>
              <p>{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Contact CTA */}
      <section
        className={`pp-contact-section ${isVisible.contact ? 'visible' : ''}`}
        data-section="contact"
        ref={addToRefs}
      >
        <div className="contact-card">
          <div className="contact-icon">💬</div>
          <h2>{currentContent.contact.title}</h2>
          <p>{currentContent.contact.text}</p>
          <button className="contact-btn">{currentContent.contact.cta}</button>
          <div className="promise-signature">— Addmix Store Team</div>
        </div>
      </section>

    </div>
  );
};

export default PrivacyPolicy;